import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { SERVER_URL } from "../../utils/serverConfig";
import "./PartnerLogin.css";

/** Animated count-up for the earnings teaser. */
const CountUp = ({ to, duration = 1400 }) => {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      // easeOutExpo
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      setVal(Math.round(to * eased));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [to, duration]);
  return <>{val.toLocaleString("en-IN")}</>;
};

/**
 * B2B Partner Portal — login page.
 *
 * Fully isolated from the standard /login flow. Authenticates against the
 * dedicated /api/v1/auth/partner-login endpoint (partner role only), stores
 * the token under the same keys the app already uses, then hard-navigates to
 * /partner so AuthContext re-hydrates the partner via /auth/me.
 */
const PartnerLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");

    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${SERVER_URL}/api/v1/auth/partner-login`,
        { email: email.trim(), password, rememberMe }
      );

      const { token, refreshToken } = res.data?.data || {};
      if (!token) {
        throw new Error("Unexpected response from server.");
      }

      localStorage.removeItem("organization");
      localStorage.removeItem("isMainAdminSwitch");
      localStorage.removeItem("mainAdminSwitchUserId");
      localStorage.setItem("token", token);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);

      window.location.assign("/partner");
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Invalid credentials. Please try again.";
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="pl2-root">
      {/* ============ LEFT — brand / value panel ============ */}
      <div className="pl2-brand">
        {/* animated gradient mesh + grid */}
        <div className="pl2-mesh pl2-mesh-a" />
        <div className="pl2-mesh pl2-mesh-b" />
        <div className="pl2-mesh pl2-mesh-c" />
        <div className="pl2-grid" />
        <div className="pl2-noise" />

        <div className="pl2-brand-inner">
          <div className="pl2-logo">
            <img
              src="/logo-partner-white.svg"
              alt="TeamGrid"
              className="pl2-logo-img"
            />
            <span className="pl2-logo-chip">Partners</span>
          </div>

          <div className="pl2-brand-mid">
            <motion.div
              className="pl2-eyebrow"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <span className="pl2-eyebrow-dot" />
              TeamGrid Partner Program
            </motion.div>

            <motion.h1
              className="pl2-title"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05, ease: "easeOut" }}
            >
              Earn <span className="pl2-title-grad">20% recurring</span>
              <br />
              on every client you bring.
            </motion.h1>

            <motion.p
              className="pl2-sub"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.12, ease: "easeOut" }}
            >
              Refer organizations to TeamGrid and earn a recurring commission on
              everything they pay — month after month. Watch it add up live.
            </motion.p>

            {/* Earnings teaser card — the "this could be you" hook */}
            <motion.div
              className="pl2-earncard"
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="pl2-earncard-glow" />
              <div className="pl2-earncard-head">
                <span className="pl2-earncard-label">
                  <span className="pl2-live-dot" />
                  Your earnings, this month
                </span>
                <span className="pl2-earncard-trend">▲ 24%</span>
              </div>
              <div className="pl2-earncard-amount">
                ₹<CountUp to={1284500} />
              </div>
              <div className="pl2-earncard-bars">
                {[42, 58, 50, 71, 64, 88, 79, 100].map((h, i) => (
                  <span
                    key={i}
                    className="pl2-bar"
                    style={{ "--h": `${h}%`, "--i": i }}
                  />
                ))}
              </div>
              <div className="pl2-earncard-foot">
                <span>12 active organizations</span>
                <span className="pl2-earncard-foot-accent">20% commission</span>
              </div>
            </motion.div>

            <motion.div
              className="pl2-features"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.32, ease: "easeOut" }}
            >
              {[
                {
                  t: "Recurring, not one-time",
                  d: "Earn every billing cycle, for as long as they stay.",
                },
                {
                  t: "Live, transparent payouts",
                  d: "See exact amounts pulled straight from billing — in ₹.",
                },
                {
                  t: "Zero overhead to run",
                  d: "We handle the product. You bring the clients.",
                },
              ].map((f) => (
                <div className="pl2-feature" key={f.t}>
                  <span className="pl2-feature-ic">
                    <svg viewBox="0 0 20 20" width="12" height="12" fill="none">
                      <path
                        d="M4 10.5l3.5 3.5L16 5.5"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <div className="pl2-feature-text">
                    <span className="pl2-feature-t">{f.t}</span>
                    <span className="pl2-feature-d">{f.d}</span>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="pl2-trust"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="pl2-foot">© 2026 TeamGrid · Partner Program</div>
          </motion.div>
        </div>
      </div>

      {/* ============ RIGHT — login card ============ */}
      <div className="pl2-form-wrap">
        <motion.div
          className="pl2-card"
          initial={{ opacity: 0, y: 22, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="pl2-card-glow" />

          <div className="pl2-card-head">
            <span className="pl2-card-badge">Welcome back</span>
            <h2 className="pl2-card-title">Sign in to your earnings</h2>
            <p className="pl2-card-sub">
              Access your partner dashboard and track your commission live.
            </p>
          </div>

          {error && (
            <motion.div
              className="pl2-error"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <svg viewBox="0 0 20 20" width="15" height="15" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" />
                <path d="M10 6v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="10" cy="14" r="1" fill="currentColor" />
              </svg>
              <span>{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="pl2-form" noValidate>
            <label className="pl2-field">
              <span className="pl2-label">Email address</span>
              <div className="pl2-input-wrap">
                <svg className="pl2-input-ic" viewBox="0 0 20 20" width="17" height="17" fill="none">
                  <rect x="2.5" y="4" width="15" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M3 6l7 5 7-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <input
                  type="email"
                  autoComplete="email"
                  className="pl2-input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            </label>

            <label className="pl2-field">
              <span className="pl2-label">Password</span>
              <div className="pl2-input-wrap pl2-has-eye">
                <svg className="pl2-input-ic" viewBox="0 0 20 20" width="17" height="17" fill="none">
                  <rect x="4" y="9" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M7 9V7a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="pl2-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="pl2-eye"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    /* eye-off */
                    <svg viewBox="0 0 22 22" width="19" height="19" fill="none">
                      <path
                        d="M3 3l16 16M9.9 5.2A7.9 7.9 0 0111 5c4.5 0 8 4 9 6a13 13 0 01-2.4 3M6.1 7.1C4 8.3 2.6 10 2 11c1 2 4.5 6 9 6a8.3 8.3 0 003.9-1"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9.4 9.6a2.3 2.3 0 003.2 3.2"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    /* eye */
                    <svg viewBox="0 0 22 22" width="19" height="19" fill="none">
                      <path
                        d="M2 11c1-2 4.5-6 9-6s8 4 9 6c-1 2-4.5 6-9 6s-8-4-9-6z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="11" cy="11" r="2.6" stroke="currentColor" strokeWidth="1.6" />
                    </svg>
                  )}
                </button>
              </div>
            </label>

            <label className="pl2-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="pl2-checkbox" />
              <span>Keep me signed in</span>
            </label>

            <button type="submit" className="pl2-submit" disabled={loading}>
              {loading ? (
                <span className="pl2-spinner" />
              ) : (
                <>
                  Sign in to portal
                  <svg viewBox="0 0 20 20" width="17" height="17" fill="none">
                    <path
                      d="M4 10h11M11 5l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="pl2-secure-row">
            <svg viewBox="0 0 20 20" width="13" height="13" fill="none">
              <rect x="4" y="9" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.4" />
              <path d="M7 9V7a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.4" />
            </svg>
            Encrypted & access-isolated partner login
          </div>

          <div className="pl2-card-foot">
            Not a partner?{" "}
            <a href="/login" className="pl2-link">
              Go to standard login
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PartnerLogin;
